<template>
    <div class="hero is-fullheight">
        <div class="hero-body">
            <div class="container has-text-centered">
                <div class="m-b-lg is-flex is-justify-content-center is-align-items-center is-flex-direction-column">
                    <ollie style="height: 6rem" custom-color="var(--p-primary-500)" />
                    <h1 class="title" style="width: max-content;">Waiting Room</h1>
                </div>
                <div class="is-flex has-content-centered is-flex-direction-column">
                    <div class="message is-flex p-5  is-align-items-center is-justify-content-center">
                        <e-icon icon="info-circle" size="is-medium" class="m-r-sm is-info" />
                        <div class="has-text-black mr-2">
                            {{ messageText }}
                        </div>
                        <spinner :size="24" color="var(--p-primary-500)" />
                    </div>
                    <div class="content is-align-items-start is-flex pt-5 pb-5" style="flex:1">
                        <div
                            v-if="loading"
                            class="is-flex is-align-items-center is-justify-content-center"
                            style="width: 100%;"
                        >
                            <spinner :size="24" color="var(--p-primary-500)" />
                            <span class="ml-2">Loading activity...</span>
                        </div>
                        <div v-else-if="loadError" class="notification is-danger" style="width: 100%;">
                            Unable to load activity. Please try again.
                        </div>
                        <div
                            v-else-if="!computedActivity"
                            class="is-flex is-align-items-center is-justify-content-center"
                            style="width: 100%;"
                        >
                            <spinner :size="24" color="var(--p-primary-500)" />
                            <span class="ml-2">Loading activity...</span>
                        </div>
                        <ul v-else style="list-style: none;">
                            <li>
                                <e-icon icon="clock" class="is-info" />
                                <span style="font-weight: 500;">Time Limit:</span> {{ timeLimitLabel }}
                            </li>
                            <li>
                                <e-icon icon="list-ul" class="is-info" />
                                <span style="font-weight: 500;">Total Questions:</span> {{ questionCount }}
                            </li>
                            <li>
                                <e-icon icon="web-section-alt" class="is-info" />
                                <span style="font-weight: 500;">Navigation:</span> {{ navigationLocked }}
                            </li>
                            <li>
                                <e-icon icon="bookmark" class="is-info" />
                                <span style="font-weight: 500;">Review:</span> {{ reviewAllowed }}
                            </li>
                            <li>
                                <e-icon icon="calculator" class="is-info" />
                                <span style="font-weight: 500;">Calculator:</span> {{ calculatorEnabled }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Spinner from '@/components/chat/system/Spinner.vue';
import Ollie from '@/components/ollie/Ollie.vue';
import Activity from '@/models/Activity.js';
import store from '@/store/Index.js';

export default {
    name: 'QuizStatus',

    components: { Ollie, Spinner },

    props: {
        status: {
            type: [String],
            required: true,
        },

        activityId: {
            type: [Number],
            required: false,
            default: null,
        },
    },

    data: () => ({
        interval: null,
        loading: false,
        loadError: false,
    }),

    computed: {
        lobby() {
            return this.status ? this.status.includes('waiting-room') : false;
        },

        // Resolve activity id from multiple sources: prop → route → user store
        resolvedActivityId() {
            return (
                this.activityId ??
                this.$route?.params?.activityId ??
                store.getters['User/get']('quiz_activity').activity.id
            );
        },

        computedActivity() {
            return store.getters['Activities/getByKey']({
                keyName: 'id',
                key: Number(this.resolvedActivityId),
            });
        },

        questionCount() {
            const countFromModule = this.computedActivity?.question_count;
            if (typeof countFromModule === 'number') return countFromModule;

            // Fallback: read from Vuex-ORM model if module state lacks question_count
            const model = Activity.find(Number(this.resolvedActivityId));
            return typeof model?.question_count === 'number' ? model.question_count : 'N/A';
        },

        content() {
            return this.getActivityField('activity_content');
        },

        uuid() {
            return this.computedActivity?.uuid ?? null;
        },

        reviewAllowed() {
            return (this.content?.review_allowed ?? false) ? 'Available' : 'Not Available';
        },

        calculatorEnabled() {
            return this.content?.calculator_enabled ? 'Available' : 'Not Available';
        },

        navigationLocked() {
            return this.content?.lockdown ? 'Locked' : 'Unlocked';
        },

        timeLimit() {
            return this.content?.time_limit ? this.content.time_limit / 60 : null;
        },

        timeLimitLabel() {
            return this.timeLimit ? `${this.timeLimit} min` : 'No Time Limit';
        },

        messageText() {
            return this.status.includes('post')
                ? 'Please wait, an instructor will review your quiz shortly.'
                : 'Please wait, an instructor will begin your next step shortly.';
        },
    },

    async mounted() {
        if (this.resolvedActivityId) {
            this.loading = true;
            this.loadError = false;
            try {
                if (!this.computedActivity) {
                    await store.dispatch('Activities/fetchOne', this.resolvedActivityId);
                }

                // Ensure question_count is present (fetch with append if missing)
                if (!this.computedActivity?.question_count) {
                    await Activity.api().get(`/api/activities/${this.resolvedActivityId}?append=question_count`);
                }
            } catch (e) {
                this.loadError = true;
            } finally {
                this.loading = false;
            }
        }
    },

    unmounted() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    },

    methods: {
        getActivityField(field) {
            if (!this.uuid) return null;
            return store.getters[`Activities/${this.uuid}/get`](field);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';

.hero {
    background-color: $white;
}

.message {
    background-color: var(--primary--light-color);
    color: black;
    border-radius: $radius;
    border-left: 4px solid $primary;
}

.content {
    text-align: left;
    border: 1px solid var(--primary-light-color);
    border-radius: $radius;
    width: 45%;

   li{
        display: flex;
        align-items: center;
        justify-content: start;
        gap: 8px;
    }

}
</style>
